/**
 * URL validation and normalization utilities
 */

/**
 * Gets initials from a name
 * @param name - The name string
 * @param maxChars - Maximum number of characters to return (default: 2)
 * @returns Uppercase letters (first name initial + last name initial), or single letter if only one word, or empty string if name is empty
 */
export function getInitial(name: string | null | undefined, maxChars: number = 2): string {
  if (!name || typeof name !== "string" || name.trim() === "") {
    return "";
  }
  
  // Ensure maxChars is at least 1
  const chars = Math.max(1, maxChars);
  
  const trimmedName = name.trim();
  const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
  
  if (nameParts.length === 0) {
    return "";
  }
  
  // If only one word, return up to maxChars letters from that word
  if (nameParts.length === 1) {
    return nameParts[0]
      .substring(0, chars)
      .toUpperCase();
  }
  
  // If multiple words, get initials from first and last words
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  
  // Calculate how many letters to take from each part
  if (chars === 1) {
    // If only 1 character requested, return first name initial
    return firstName.charAt(0).toUpperCase();
  }
  
  // For 2+ characters, take first letter of first name and remaining from last name
  const firstInitial = firstName.charAt(0).toUpperCase();
  const remainingChars = chars - 1;
  const lastInitials = lastName.substring(0, remainingChars).toUpperCase();
  
  return firstInitial + lastInitials;
}

/**
 * Gets the appropriate font size in pixels based on container size
 * Font size is calculated as approximately 80-90% of container size for maximum visibility
 * @param size - The size of the container in pixels (width or height, assuming square)
 * @returns Font size in pixels as a string
 */
export function getInitialFontSize(size: number): string {
  // Calculate font size as percentage of container size (approximately 80-90%)
  // This makes the initial very prominent and fills the container well
  if(size <= 12) {
    return "12px";
  }
  if (size <= 16) {
    return "14px";
  } else if (size <= 20) {
    return "18px";
  } else if (size <= 28) {
    return "24px";
  } else if (size <= 32) {
    // Meeting attendees in step 1 (w-8 h-8 = 32px)
    return "28px";
  } else if (size <= 36) {
    // Timeline attendees (w-9 h-9 = 36px)
    return "32px";
  } else if (size <= 48) {
    return "42px";
  } else if (size <= 64) {
    return "56px";
  } else if (size <= 80) {
    // Header company logo (w-20 h-20 = 80px)
    return "72px";
  } else if (size <= 100) {
    return "88px";
  } else {
    // For sizes > 100px, use large custom size
    return "108px";
  }
}

/**
 * Validates if a URL is a valid HTTP/HTTPS URL with a proper hostname
 * @param url - The URL string to validate
 * @returns true if the URL is valid, false otherwise
 */
export function isValidImageUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string" || url.trim() === "") {
    return false;
  }

  try {
    const urlObj = new URL(url);
    
    // Must be http or https protocol
    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return false;
    }

    // Must have a valid hostname (contains a dot, indicating a proper domain)
    // Exclude common invalid patterns like "logo.png", "image.png", etc.
    const hostname = urlObj.hostname.toLowerCase();
    if (!hostname || !hostname.includes(".")) {
      return false;
    }

    // Exclude common invalid hostnames
    const invalidHostnames = ["logo.png", "image.png", "placeholder.png", "avatar.png"];
    if (invalidHostnames.includes(hostname)) {
      return false;
    }

    return true;
  } catch {
    // If URL parsing fails, it's not a valid URL
    return false;
  }
}

/**
 * Gets a valid image URL, returning null if invalid (to show initials instead)
 * @param url - The URL string to validate
 * @returns A valid URL string or null if invalid
 */
export function getValidImageUrl(
  url: string | null | undefined
): string | null {
  if (isValidImageUrl(url)) {
    return url!;
  }
  return null;
}

/**
 * Gets a valid avatar URL, returning null if invalid (to show initials instead)
 * @param url - The URL string to validate
 * @returns A valid URL string or null if invalid
 */
export function getValidAvatarUrl(url: string | null | undefined): string | null {
  return getValidImageUrl(url);
}

/**
 * Gets a valid logo URL, returning null if invalid (to show initials instead)
 * @param url - The URL string to validate
 * @returns A valid URL string or null if invalid
 */
export function getValidLogoUrl(url: string | null | undefined): string | null {
  return getValidImageUrl(url);
}

/**
 * Gets the fallback logo path for a marketplace name when logo_url is not available
 * @param marketplaceName - The name of the marketplace (case-insensitive)
 * @returns The path to the logo image in the public folder, or null if no match
 */
export function getMarketplaceFallbackLogo(marketplaceName: string | null | undefined): string | null {
  if (!marketplaceName || typeof marketplaceName !== "string") return null;

  const normalizedName = marketplaceName.toLowerCase().trim();

  // Map marketplace names to their logo files
  const logoMap: Record<string, string> = {
    "amazon": "/Amazon.png",
    "otto": "/Otto.png",
    "laredoute": "/LaRedoute.png",
    "la redoute": "/LaRedoute.png",
    "ebay": "/ebay.png",
  };

  // Check exact match first
  if (logoMap[normalizedName]) {
    return logoMap[normalizedName];
  }

  // Check if name contains any of the marketplace keywords
  for (const [key, logoPath] of Object.entries(logoMap)) {
    if (normalizedName.includes(key)) {
      return logoPath;
    }
  }

  return null;
}

/**
 * Gets a display name for a source object, with fallback logic
 * Priority: title > name > extracted from URL > fallback string
 * @param source - Source object that may have title, name, url, and optionally type
 * @param fallback - Fallback string to return if no valid name can be extracted (default: "Source")
 * @returns A display name string for the source
 */
export function getSourceDisplayName(
  source: {
    title?: string | null;
    name?: string | null;
    url?: string | null;
    type?: string | null;
  },
  fallback: string = "Source"
): string {

 
  if (source.title && source.title.trim()) {
    return source.title.trim();
  }

  if (source.name && source.name.trim()) {
    return source.name.trim();
  }

  if (source.url && source.url.trim()) {
    try {
      const url = new URL(source.url);
      // Get domain name (e.g., "example.com" from "https://www.example.com/path")
      let domain = url.hostname.replace(/^www\./, "");
      // Capitalize first letter and remove common TLDs for cleaner display
      return domain.split(".")[0].charAt(0).toUpperCase() + domain.split(".")[0].slice(1);
    } catch {
      return source.url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
    }
  }
  return fallback;
}

